import { Helmet } from 'react-helmet-async'
import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { StoreSelectionProvider, useStoreSelection } from '@/context/StoreSelectionContext'
import { StaticMapBackground } from '@/components/map/StaticMapBackground'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { ToastProvider } from '@/components/ui/Toast'
import { Provider, useSelector, useDispatch } from 'react-redux'
import { store } from './store'
import { selectIsDarkMode, updateSystemPreference } from './nightModeSlice'
import { selectTheme, loadThemeFromWindow } from './theamSlice'
import { loadRestaurantFromWindow } from './restaurantSlice'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Loading overlay for optimistic UI
function LoadingOverlay({ isVisible }: { isVisible: boolean }) {
  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-surface-primary/80 backdrop-blur-sm animate-fade-in">
      <div className="flex flex-col items-center gap-4">
        <div className="size-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm/5 text-text-secondary">Loading store...</p>
      </div>
    </div>
  )
}

function AppContent() {
  const { state } = useStoreSelection()
  const [isLoading, setIsLoading] = useState(false)
  const location = useLocation()
  const darkMode = useSelector(selectIsDarkMode)
  const dispatch = useDispatch()
  const theme = useSelector(selectTheme)

  // Load theme and restaurant from window globals on mount
  useEffect(() => {
    dispatch(loadThemeFromWindow())
    dispatch(loadRestaurantFromWindow())
  }, [dispatch])

  // Apply dynamic theme colours from window/slice as CSS variables
  useEffect(() => {
    const root = document.documentElement
    if (theme?.primaryThemeColour) {
      root.style.setProperty('--color-brand-primary', theme.primaryThemeColour)
    } else {
      root.style.removeProperty('--color-brand-primary')
    }

    if (theme?.secondaryThemeColour) {
      root.style.setProperty('--color-brand-secondary', theme.secondaryThemeColour)
    } else {
      root.style.removeProperty('--color-brand-secondary')
    }
  }, [theme?.primaryThemeColour, theme?.secondaryThemeColour])

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [darkMode])

  // Listen for system preference changes and update Redux if in auto-detect mode
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      store.dispatch(updateSystemPreference(e.matches))
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Simulate optimistic loading when selecting a store
  useEffect(() => {
    if (state.selectedStoreId) {
      setIsLoading(true)
      const timeout = setTimeout(() => {
        setIsLoading(false)
        console.log('Navigating to store menu:', state.selectedStoreId)
      }, 800)
      return () => clearTimeout(timeout)
    }
  }, [state.selectedStoreId])

  // Check if we're on the home page
  const isHomePage = location.pathname === '/'

  return (
    <>
      <Helmet>
        <title>Select Your Store | MoVida</title>
        <meta 
          name="description" 
          content="Choose your nearest MoVida location for pickup or delivery. Fresh Spanish tapas and wine, ready when you are." 
        />
        <meta property="og:title" content="Select Your Store | MoVida" />
        <meta 
          property="og:description" 
          content="Choose your nearest MoVida location for pickup or delivery. Fresh Spanish tapas and wine, ready when you are." 
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://movida.com.au/order" />
        
        {/* Theme color for browser chrome */}
        <meta name="theme-color" content={darkMode ? '#1e1b18' : '#8b2d10'} />
        
        {/* Structured data for local business */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Restaurant',
            name: 'MoVida',
            description: 'Spanish tapas and wine bar with multiple locations across Melbourne',
            servesCuisine: 'Spanish',
            priceRange: '$$',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Melbourne',
              addressRegion: 'VIC',
              addressCountry: 'AU',
            },
            hasMenu: 'https://movida.com.au/menu',
            acceptsReservations: true,
          })}
        </script>
      </Helmet>

      {/* Static Map Background - only show on home page */}
      {isHomePage && <StaticMapBackground />}

      {/* Main Content with Routes */}
      <div className="relative z-10">
        <Outlet />
      </div>

      {/* Loading Overlay for Store Selection */}
      <LoadingOverlay isVisible={isLoading} />
    </>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <StoreSelectionProvider>
          <Provider store={store}>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </Provider>
        </StoreSelectionProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
